// Single source of truth for "now" / "today" across the SmashZone app.
//
// SmashZone is Brunei-native (UTC+8, no DST). We do NOT honour the
// browser-local timezone for any of our business logic — an admin opening
// the dashboard from London at 11pm would otherwise see "today" resolve to
// the previous day in Brunei. This module is the single import point that
// makes the app behave correctly regardless of where the operator's
// browser thinks they are.
//
// Mirrors backend `utils/helpers.py::get_brunei_now / get_brunei_date`.

const BRUNEI_TZ = 'Asia/Brunei';

// Returns today as `YYYY-MM-DD` in Brunei. Use for: default date pickers,
// "is this booking today?" checks, audit-log filters.
export const bruneiToday = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: BRUNEI_TZ }).format(new Date());

// Returns the date object converted into Brunei wallclock components. Use
// when you need a specific date offset (e.g. "max bookable = today + 30d").
export const bruneiNow = () => {
  // Intl.DateTimeFormat doesn't return a Date — we reconstruct one in the
  // browser's tz that has Brunei wallclock values. Good for arithmetic only.
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: BRUNEI_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).formatToParts(new Date()).reduce((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value;
    return acc;
  }, {});
  // 'YYYY-MM-DDTHH:mm:ss' parsed as local time in the browser. NOT a
  // timezone-correct Date object — only safe for date-arithmetic helpers
  // below. For display use bruneiPretty / bruneiFormatTs.
  return new Date(`${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`);
};

// Returns the date `n` days from today as `YYYY-MM-DD` in Brunei.
// Used by booking flow for "max bookable date = today + N".
export const bruneiDateOffset = (days) => {
  const todayIso = bruneiToday();             // e.g. "2026-04-30"
  // Anchor at noon UTC+8 to avoid edge cases — adding `days` * 24h then
  // re-formatting in Brunei tz gives the correct calendar offset.
  const anchor = new Date(`${todayIso}T12:00:00+08:00`);
  anchor.setUTCDate(anchor.getUTCDate() + days);
  return new Intl.DateTimeFormat('en-CA', { timeZone: BRUNEI_TZ }).format(anchor);
};

// Pretty date label, e.g. "Mon, Apr 27" — used in glance cards.
export const bruneiPretty = (date = new Date()) =>
  new Intl.DateTimeFormat('en-US', {
    timeZone: BRUNEI_TZ,
    weekday: 'short', month: 'short', day: 'numeric',
  }).format(date);

// Render any ISO timestamp string as `DD/MM/YYYY, HH:MM:SS` in Brunei.
// Used by audit logs and any "when did this happen" UI.
export const bruneiFormatTs = (iso) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: BRUNEI_TZ,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

// Pretty timestamp ("Apr 27, 2026, 14:30") — used in security/integration
// screens where the year matters.
export const bruneiFormatLongTs = (iso) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: BRUNEI_TZ,
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
      hour12: false,
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

// "5 minutes ago" / "2 hours ago" relative time, anchored to Brunei now.
// Used by PaymentPage to show "Started X minutes ago".
export const bruneiTimeAgo = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now(); // tz-irrelevant — we just diff two epoch ms.
  const seconds = Math.floor((now - then) / 1000);
  if (seconds < 60) return 'just now';
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};
